import { User } from 'api/models';
import { Task } from 'api/tasks';
import { TemplateId } from 'data/templates/api';
import { HttpClient } from '../http-client';
import { Space } from './space';

interface Thumbnail {
  href: string;
  media: { width: number; height: number };
  rel: string[];
  type: 'image';
}

interface Icon {
  href: string;
  rel: string[];
  type: string;
}

export enum SpaceTabDocumentType {
  RocketlaneDocument = 'ROCKETLANE_DOCUMENT',
  EmbedableDocument = 'GOOGLE_DOCUMENT', // TODO:: @kanny/@sha to change this type.
}

export interface SpaceTabEmbedMetadata {
  html: string;
  links: { thumbnail: Thumbnail[]; icon: Icon[] };
  meta: { site: string; title: string; canonical: string };
  rel: string[];
  url: string;
}

export interface SpaceTab {
  spaceTabId: number;
  spaceTabType: SpaceTabDocumentType;
  contentPath: string;
  commentThreadId: number;
  metaData?: Partial<SpaceTabEmbedMetadata>;
  space: Space;
  spaceTabTitle: string;
  url: string;
  taskList: Task[];
  createdAt: number;
  createdBy: User;
}

export enum SpaceTabType {
  Project = 'PROJECT',
  ProjectTemplate = 'PROJECT_TEMPLATE',
  DocumentTemplate = 'DOCUMENT_TEMPLATE',
}

export interface GetSpaceIdResponse {
  space: {
    spaceId: number;
  };
  spaceTabId: number;
  spaceTabType: string;
  url: string;
  spaceTabTitle: string;
  commentThreadId: number;
  account: {
    accountId: number;
  };
  createdBy: {
    userId: number;
    identityType: string;
  };
  createdAt: number;
}

// For template space tabs and normal space tabs
export interface SpaceTabSkelton
  extends AtLeast<
    SpaceTab,
    'spaceTabId' | 'spaceTabTitle' | 'spaceTabType' | 'space'
  > {
  project?: {
    projectId: number;
  };
  type?: SpaceTabType;
}

export type UpdateSpaceTabPayload = Pick<SpaceTab, 'url' | 'spaceTabTitle'>;

export interface CreateSpaceTabPayload
  extends Pick<SpaceTab, 'spaceTabType' | 'spaceTabTitle'> {
  space: Pick<Space, 'spaceId'>;
}

export interface CreateSpaceEmbedPayload
  extends Pick<SpaceTab, 'url'>,
    Partial<Pick<SpaceTab, 'spaceTabTitle'>> {
  space: Pick<Space, 'spaceId'>;
}

export type CreateSpaceFromTemplatepayload = {
  space: Pick<Space, 'spaceId'>;
  entitySource: {
    sourceId: TemplateId;
  };
};

class SpaceTabHttpClient extends HttpClient {
  public async getSpaceTab(spaceTabId: number): Promise<SpaceTab> {
    return this.get<SpaceTab>(`/space-tabs/${spaceTabId}`);
  }

  public async getSpaceId(spaceTabId: number): Promise<GetSpaceIdResponse> {
    return this.get<GetSpaceIdResponse>(
      `/space-tabs/${spaceTabId}/get-space-id`,
    );
  }

  // TODO: @Krishna remove ? once the sagas are removed
  public async createSpaceTab(
    payload?:
      | CreateSpaceTabPayload
      | CreateSpaceEmbedPayload
      | CreateSpaceFromTemplatepayload,
  ): Promise<SpaceTab> {
    return this.post('/space-tabs', payload);
  }

  public async updateSpaceTab(
    spaceTabId: number,
    data: UpdateSpaceTabPayload,
  ): Promise<SpaceTab> {
    return this.put(`/space-tabs/${spaceTabId}`, data);
  }

  public async deleteSpaceTab(spaceTabId: number): Promise<SpaceTab> {
    return this.delete(`/space-tabs/${spaceTabId}`);
  }

  public async updateLastSeen(
    projectId: number,
    spaceTabId: number,
  ): Promise<void> {
    return this.post(
      `/projects/${projectId}/space-tabs/${spaceTabId}/last-seen`,
    );
  }
}

export const SpaceTabAPI = new SpaceTabHttpClient();
